import React, { useEffect, useState } from "react";
import { Download, X, Smartphone, Share, MoreVertical, Plus } from "lucide-react";
import { track } from "../lib/telemetry";

const DISMISS_KEY = "td_install_dismissed_at";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // re-show after 7 days
const ENGAGED_KEY = "td_engaged"; // sessionStorage flag — sticky across nav

/**
 * Detect whether the app is already running as an installed PWA.
 * Covers iOS Safari (navigator.standalone), Chrome/Edge desktop & Android
 * (display-mode: standalone), and the legacy webkit form.
 */
function isStandalone() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  if (window.navigator && window.navigator.standalone === true) return true;
  return false;
}

function isMobile() {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod|Mobile|webOS|BlackBerry/i.test(navigator.userAgent);
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent) && !window.MSStream;
}

function recentlyDismissed() {
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  const at = parseInt(raw, 10);
  if (!at) return false;
  return Date.now() - at < DISMISS_TTL_MS;
}

function dismissNow() {
  localStorage.setItem(DISMISS_KEY, String(Date.now()));
}

function IOSHowToModal({ onClose }) {
  return (
    <div
      className="fixed inset-0 z-[1300] bg-brand-text/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="install-howto-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-brand-surface w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 animate-fade-up max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-heading text-xl text-brand-text flex items-center gap-2">
            <Smartphone size={18} className="text-brand-primary" />
            Add to Home Screen
          </h3>
          <button onClick={onClose} className="text-brand-subtle" aria-label="Close" data-testid="install-howto-close">
            <X size={20} />
          </button>
        </div>
        <p className="text-sm text-brand-subtle">
          iOS doesn&apos;t support one-tap install, but it takes 3 taps in Safari:
        </p>

        <ol className="mt-4 space-y-3 text-sm text-brand-text">
          <li className="flex items-start gap-3">
            <span className="shrink-0 w-7 h-7 rounded-full bg-brand-primary text-brand-sand flex items-center justify-center font-bold text-xs">1</span>
            <span>
              Make sure you&apos;re in <strong>Safari</strong> (not Chrome). Tap the{" "}
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-brand-sand border border-brand-border">
                <Share size={13} /> Share
              </span>{" "}
              icon at the bottom of the screen.
            </span>
          </li>
          <li className="flex items-start gap-3">
            <span className="shrink-0 w-7 h-7 rounded-full bg-brand-primary text-brand-sand flex items-center justify-center font-bold text-xs">2</span>
            <span>
              Scroll down and tap{" "}
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-brand-sand border border-brand-border">
                <Plus size={13} /> Add to Home Screen
              </span>
              .
            </span>
          </li>
          <li className="flex items-start gap-3">
            <span className="shrink-0 w-7 h-7 rounded-full bg-brand-primary text-brand-sand flex items-center justify-center font-bold text-xs">3</span>
            <span>Tap <strong>Add</strong> in the top-right — TrailDog now lives on your home screen.</span>
          </li>
        </ol>

        <div className="td-card mt-5 p-4 bg-brand-sand">
          <div className="td-section-label">Android · Chrome</div>
          <p className="text-sm text-brand-text mt-1">
            Tap the{" "}
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-brand-surface border border-brand-border">
              <MoreVertical size={13} /> menu
            </span>{" "}
            in the top-right of Chrome and choose <strong>&quot;Install app&quot;</strong> or <strong>&quot;Add to Home screen&quot;</strong>.
          </p>
        </div>

        <button
          data-testid="install-howto-done"
          onClick={onClose}
          className="td-btn-primary w-full mt-5"
        >
          Got it
        </button>
      </div>
    </div>
  );
}

export default function InstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showHowTo, setShowHowTo] = useState(false);
  const [engaged, setEngaged] = useState(() => {
    try { return sessionStorage.getItem(ENGAGED_KEY) === "true"; } catch { return false; }
  });

  // Telemetry: fire `install_prompt_shown` once per session when the banner first appears.
  useEffect(() => {
    if (visible) {
      track("install_prompt_shown", { platform: isIOS() ? "ios" : "android" });
    }
  }, [visible]);

  // Listen for the engagement signal dispatched by high-intent UI (e.g.
  // tapping "Open on map"). Until that fires, the install banner stays
  // dormant even if `beforeinstallprompt` has already arrived.
  useEffect(() => {
    const onEngage = () => {
      try { sessionStorage.setItem(ENGAGED_KEY, "true"); } catch {}
      setEngaged(true);
    };
    window.addEventListener("td-engagement", onEngage);
    return () => window.removeEventListener("td-engagement", onEngage);
  }, []);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed() || !isMobile()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Only surface immediately if the user has already engaged this
      // session; otherwise keep the prompt in our pocket until they do.
      if (engaged) setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    const onInstalled = () => {
      track("install_completed", { platform: isIOS() ? "ios" : "android" });
      setVisible(false);
      setDeferredPrompt(null);
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [engaged]);

  // When engagement happens later, surface a banner if we already
  // have a deferred prompt — or, on iOS Safari, show the howto variant.
  useEffect(() => {
    if (!engaged) return;
    if (isStandalone() || recentlyDismissed() || !isMobile()) return;
    if (deferredPrompt || isIOS()) {
      const t = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(t);
    }
  }, [engaged, deferredPrompt]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      const choice = await deferredPrompt.userChoice;
      const outcome = (choice && choice.outcome) || "unknown";
      track(outcome === "accepted" ? "install_accepted" : "install_outcome_dismissed", {
        outcome,
        platform: "android",
      });
    } catch {
      track("install_outcome_dismissed", { outcome: "error", platform: "android" });
    }
    setDeferredPrompt(null);
    setVisible(false);
  };

  const handleLater = () => {
    track("install_dismissed", { platform: isIOS() ? "ios" : "android" });
    dismissNow();
    setVisible(false);
  };

  const openHowTo = () => {
    track("install_howto_opened", { platform: "ios" });
    setShowHowTo(true);
  };

  if (!visible) return null;

  const iosMode = isIOS() && !deferredPrompt;

  return (
    <>
      <div
        data-testid="install-banner"
        className="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:right-4 sm:max-w-sm z-[1200] td-card p-4 shadow-lg flex items-center gap-3 animate-fade-up"
        role="dialog"
        aria-label="Install TrailDog"
      >
        <div className="shrink-0 w-10 h-10 rounded-2xl bg-brand-primary/15 text-brand-primary flex items-center justify-center">
          <Download size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-brand-text">Planning more hikes?</div>
          <div className="text-xs text-brand-subtle mt-0.5 truncate">
            Add to home screen for faster access on your next walk.
          </div>
        </div>
        <div className="flex flex-col gap-1.5 shrink-0">
          {iosMode ? (
            <button
              data-testid="install-howto-btn"
              onClick={openHowTo}
              className="td-btn-primary !py-1.5 !px-3 !text-xs whitespace-nowrap"
            >
              How to install
            </button>
          ) : (
            <button
              data-testid="install-now-btn"
              onClick={handleInstall}
              className="td-btn-primary !py-1.5 !px-3 !text-xs whitespace-nowrap"
            >
              Install Now
            </button>
          )}
          <button
            data-testid="install-later-btn"
            onClick={handleLater}
            className="text-[11px] text-brand-subtle hover:text-brand-text whitespace-nowrap"
          >
            Maybe Later
          </button>
        </div>
      </div>

      {showHowTo && <IOSHowToModal onClose={() => setShowHowTo(false)} />}
    </>
  );
}
